import { Page } from "../enums/Page";
import { html, css } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { PuzzleService } from "../services/PuzzleService";

export class PuzzleComponent extends HTMLElement {
    private readonly _gameEventService: GameEventService = new GameEventService();
    private readonly _puzzleService: PuzzleService = new PuzzleService();
    private readonly _word: string = "AIDANHASDRUGGEDYOU".toUpperCase();
    private _guessedLetters: Set<string> = new Set();
    private _wrongGuesses: number = 0;
    private readonly _maxWrongGuesses: number = 3;

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render().catch((error: unknown) => console.error("Render error:", error));
    }

    private async render(): Promise<void> {
        if (!this.shadowRoot) return;

        const displayWord: string = this._word
            .split("")
            .map(letter => (letter === " " ? " " : this._guessedLetters.has(letter) ? letter : "_"))
            .join(" ");

        const gameOver: boolean = this._wrongGuesses >= this._maxWrongGuesses;
        const isWin: boolean = !displayWord.includes("_");

        if (isWin) {
            try {
                // Call solvePuzzle when the user wins
                const solved: boolean = (await this._puzzleService.solvePuzzle("AIDANHASDRUGGEDYOU")).solved;
                if (solved) {
                    console.log("Puzzle officially solved via API.");
                }
                else {
                    console.log("API response: Puzzle not solved.");
                }
            }
            catch (error: unknown) {
                console.error("Error while solving puzzle:", error);
            }
        }

        const element: HTMLElement = html`
        <div>
            <style>${styles}</style>
            <div class="puzzle-header">
                <h2>Hangman</h2>
                <h2>You have ${3 - this._wrongGuesses} guesses left</h2>
            </div>
            <div class="puzzle-word">${displayWord}</div>
            <div class="puzzle-letters">
                ${gameOver || isWin ? "" : this.renderLetterButtons()} <!-- Safely calling renderLetterButtons -->
            </div>
            <div class="puzzle-status">
                ${gameOver ? "You lost!" : ""}
                ${isWin ? "You have solved the truth..." : ""}
            </div>
            <div class="navigation">
                
              <!--  <button id="solve-puzzle">Solve</button>  Added the Solve button -->
                ${gameOver || isWin ? "<button id=\"continue-puzzle\">Continue</button>" : ""}
            </div>
        </div>
        `;

        element.querySelector("#close-puzzle")?.addEventListener("click", () => {
            this._gameEventService.switchPage(Page.Canvas);
        });

        element.querySelector("#continue-puzzle")?.addEventListener("click", () => {
            this._gameEventService.switchPage(Page.Canvas);
        });

        // Solve puzzle button click event handler
        element.querySelector("#solve-puzzle")?.addEventListener("click", async () => {
            try {
                const solved: boolean = (await this._puzzleService.solvePuzzle("AIDANHASDRUGGEDYOU")).solved;
                if (solved) {
                    console.log("Puzzle solved via button click!");
                }
                else {
                    console.log("API response: Puzzle not solved.");
                }
            }
            catch (error: unknown) {
                console.error("Error while solving puzzle:", error);
            }
        });

        element.querySelectorAll(".letter-button").forEach(button => {
            button.addEventListener("click", async event => { // Mark as async
                const target: HTMLButtonElement = event.target as HTMLButtonElement; // Explicit type annotation
                await this.handleGuess(target.textContent || ""); // Await the async method
            });
        });

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }
        this.shadowRoot.append(element);
    }

    // Define the renderLetterButtons method here
    private renderLetterButtons(): string {
        const alphabet: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        return alphabet.split("").map(letter => {
            return `<button class="letter-button" ${this._guessedLetters.has(letter) ? "disabled" : ""}>${letter}</button>`;
        }).join(" ");
    }

    private async handleGuess(letter: string): Promise<void> {
        if (!letter || this._guessedLetters.has(letter)) return;

        this._guessedLetters.add(letter);
        if (!this._word.includes(letter)) {
            this._wrongGuesses++;
        }
        await this.render();
    }
}

const styles: string = css`
 :host {
    display: block;
    text-align: center;
    padding: 20px;
    background-color: #1a1a1a;
    color: #d90429;
    font-family: "Courier New", monospace;
    text-shadow: 2px 2px 4px rgba(255, 0, 0, 0.5);
}

.puzzle-header h2 {
    font-size: 32px;
    letter-spacing: 2px;
    color: #ff0000;
    text-shadow: 4px 4px 6px rgba(255, 0, 0, 0.6);
    animation: flicker 1.5s infinite alternate;
}

.puzzle-word {
    font-size: 28px;
    margin-bottom: 20px;
    background: rgba(0, 0, 0, 0.6);
    padding: 10px;
    display: inline-block;
    border: 2px dashed #ff0000;
    letter-spacing: 5px;
    box-shadow: 0px 0px 15px rgba(255, 0, 0, 0.8);
}

.puzzle-letters {
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
}

.letter-button {
    background: #222;
    color: #ff0000;
    border: 1px solid #ff0000;
    padding: 10px;
    margin: 3px;
    font-size: 16px;
    cursor: pointer;
    transition: transform 0.2s, background 0.2s;
}

.letter-button:hover {
    background: #d90429;
    color: white;
    transform: scale(1.1);
}

.letter-button:disabled {
    background: #444;
    color: #888;
    border-color: #666;
    cursor: not-allowed;
}

.puzzle-status {
    font-size: 20px;
    font-weight: bold;
    margin-top: 10px;
    color: #ff0000;
    text-shadow: 2px 2px 5px rgba(255, 0, 0, 0.7);
}

.navigation {
    margin-top: 20px;
}

.navigation button {
    background: #4c0000;
    color: white;
    border: 2px solid #ff0000;
    padding: 12px 25px;
    font-size: 16px;
    cursor: pointer;
    margin: 5px;
    font-weight: bold;
    text-transform: uppercase;
    box-shadow: 0px 0px 10px rgba(255, 0, 0, 0.6);
    transition: transform 0.3s, background 0.3s;
}

.navigation button:hover {
    background: #ff0000;
    transform: scale(1.1);
}

.navigation button:active {
    transform: scale(0.95);
}

/* Flicker effect for horror aesthetic */
@keyframes flicker {
    0% {
        opacity: 0.8;
        text-shadow: 2px 2px 10px rgba(255, 0, 0, 0.8);
    }
    100% {
        opacity: 1;
        text-shadow: 4px 4px 15px rgba(255, 0, 0, 1);
    }
}
`;

customElements.define("game-puzzle", PuzzleComponent);
